// src/components/JournalEntriesScreen.js
import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Button,
  Card,
  Backdrop,
  CircularProgress,
  Snackbar,
  IconButton,
} from "@mui/material";
import {
  useGetJournalEntriesQuery,
  useMigrateJournalEntryMutation,
} from "../../../redux/Slices/journal-entries-api";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { arSA } from "@mui/material/locale";
import { arSD } from "@mui/x-data-grid/locales"; // For DataGrid localization
import CssBaseline from "@mui/material/CssBaseline";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GenericAddModel from "components/genericAddModel";
import CreateJournalEntries from "./CreateJournalEntries";
import JournalEntriesModal from "./JournalEntriesModal";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import CloseIcon from "@mui/icons-material/Close";
import { set } from "date-fns";
import { el } from "date-fns/locale";
// create JournalEntries report
const JournalEntriesScreen = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [message, setMessage] = useState("");
  const action = (
    <React.Fragment>
      <IconButton size="small" aria-label="close" color="inherit" onClick={() => setNotify(false)}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const [notify, setNotify] = useState(false);
  const [showModalOpen, setShowModalOpen] = useState(false);
  // Fetch journal entries using RTK Query
  const { data, error, isLoading } = useGetJournalEntriesQuery({
    page,
    per_page: pageSize,
  });
  const [migrateJournalEntry, { isLoading: isMigrating }] = useMigrateJournalEntryMutation();
  const [cancelJournalEntry, { isLoading: isCancelling }] = useMigrateJournalEntryMutation();
  const handleEditClick = (account) => {
    setCurrentAccount(account);
    setModalOpen(true);
  };
  const showJournalEntriesHandler = (account) => {
    setCurrentAccount(account);
    setShowModalOpen(true);
  };
  // Migrate journal entry handler
  const migrateJournalEntryHandler = async (account) => {
    const response = await migrateJournalEntry(account.id).unwrap();

    if (response.success) {
      setMessage(response.message || "تم ترحيل قيد اليومية بنجاح");
    } else {
      setMessage(response.message || "فشل ترحيل قيد اليومية");
    }

    setNotify(true);
    // Call the migrateJournalEntry mutation here
  };
  // Cancel journal entry handler
  const cancelJournalEntryHandler = async (account) => {
    const response = await cancelJournalEntry(account.id).unwrap();

    if (response.success) {
      setMessage(response.message || "تم الغاء قيد اليومية بنجاح");
    } else {
      setMessage(response.message || "فشل الغاء قيد اليومية");
    }

    setNotify(true);
    // Call the migrateJournalEntry mutation here
  };
  if (isMigrating || isCancelling || isLoading ) {
    return (
      <Backdrop open={true}>
        <CircularProgress />
      </Backdrop>
    );
  }

  const columns = [
    { field: "number", headerName: "رقم القيد", flex: 1 },
    { field: "description", headerName: "الوصف", flex: 1 },
    { field: "total_debit", headerName: "إجمالي المدين", flex: 1 },
    { field: "total_credit", headerName: "إجمالي الدائن", flex: 1 },
    { field: "fiscal_period_name", headerName: "الفترة المالية", flex: 1 },
    { field: "status_name", headerName: "الحالة", flex: 0.5 },
    { field: "created_at", headerName: "تاريخ الإنشاء", flex: 1 },
    {
      feild: "actions",
      headerName: "الاجراءات",
      flex: 1.5,
      renderCell: (params) => (
        <Box sx={{ display: "flex" }}>
          <Button
            sx={{ minWidth: "0px", padding: "14px", color: "text.main" }}
            onClick={() => handleEditClick(params.row)}
          >
            <EditIcon />
          </Button>
          <Button
            sx={{ minWidth: "0px", padding: "14px", color: "text.main" }}
            onClick={() => showJournalEntriesHandler(params.row)}
          >
            <VisibilityIcon />
          </Button>
          <Button
            sx={{ minWidth: "0px", padding: "14px", color: "text.main" }}
            onClick={() => migrateJournalEntryHandler(params.row)}
          >
            <DriveFileMoveIcon />
          </Button>
          <Button
            sx={{ minWidth: "0px", padding: "14px", color: "text.main" }}
            onClick={() => cancelJournalEntryHandler(params.row)}
          >
            <DeleteIcon />
          </Button>
        </Box>
      ),
    },
  ];

  const rows = data?.data || [];

  const handlePageChange = (newPage) => {
    setPage(newPage + 1); // DataGrid page is 0-indexed
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

 
  if (error) return <Typography>حدث خطأ: {error.message}</Typography>;

  return (
    <>
      <Backdrop open={isLoading} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={notify}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setNotify(false)}
        message={message}
        action={action}
      />
      <GenericAddModel open={modalOpen} onClose={() => setModalOpen(false)}>
        <CreateJournalEntries
          onSuccess={() => setModalOpen(false)}
          onClose={() => setModalOpen(false)}
        />
      </GenericAddModel>
      <GenericAddModel open={showModalOpen} onClose={() => setShowModalOpen(false)}>
        <JournalEntriesModal
          journalEntry={currentAccount}
          onSuccess={() => setShowModalOpen(false)}
          onClose={() => setShowModalOpen(false)}
        />
      </GenericAddModel>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            borderRadius="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(90deg, #ca9937, #e0b15a)",
              boxShadow: 3,
            }}
          >
            <MDTypography variant="h6" color="white">
              قيود اليومية
            </MDTypography>
            <MDButton
              variant="contained"
              color="white"
              startIcon={<AddIcon />}
              sx={{ color: "white" }}
              onClick={() => setModalOpen(true)}
            >
              إنشاء قيد
            </MDButton>
          </MDBox>
          <CssBaseline />
          <Box sx={{ height: "80vh", width: "100%", padding: 2, dir: "rtl",  }}>
            <DataGrid
              rows={rows}
              columns={columns}
             sx={{
    color:"text.main",
    // 1. General Grid Background
    bgcolor: "inherit", // Change this to your desired grid background color
    border: "none",
    borderTop: "none",
    // 2. Customizing the Header Background
    '& .MuiDataGrid-columnHeaders': {
      bgcolor: "#f5f5f5", // Change this to your desired header color
      color:"text.main",
      borderBottom: "none !important ",
      border:"none !important",
     // Optional: Adds a separator below headers
    },
    // Removing default focus outlines (from your original code)
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
      outlineOffset: 0,
    },
    
    // Removing the default cell border if you want a cleaner "card" look
    '& .MuiDataGrid-cell': {
      borderBottom: 'none !important',
      border:"none !important", 
      color:"text.main",
      display:"flex",
      
      alignItems:"center",
    },
    "& .MuiToolbar-root-MuiTablePagination-toolbar": {
      color:"text.main",
    },
    "& .MuiDataGrid-footerContainer": {
      color:"text.main",
    },
    "& ..MuiDataGrid-withBorderColor": {
     borderColor:"text.border",
    },
    "& .MuiTablePagination-root":{
      color:"text.main",
    },
    "& .MuiButtonBase-root-MuiIconButton-root":{
      color:"text.main",
    },
    "& .MuiButtonBase-root-MuiIconButton-root.Mui-disabled":{
      color:"text.border",
    },
   
    
    
  }}
              getRowId={(row) => row.id}
              paginationMode="server"
              rowCount={data?.pagination?.total || 0}
              pageSizeOptions={[10, 20, 50]}
              paginationModel={{ page: page - 1, pageSize }}
              onPaginationModelChange={(model) => {
                handlePageChange(model.page);
                handlePageSizeChange(model.pageSize);
              }}
              localeText={arSD.components.MuiDataGrid.defaultProps.localeText}
              disableRowSelectionOnClick
            />
          </Box>
        </Card>
      </MDBox>
    </>
  );
};

export default JournalEntriesScreen;
